/**
 * Importing the entries of a reference table from a file (proposal §5): the
 * price list has 87 rows and lives in an Excel, so it is loaded from the
 * application instead of being written by hand or shipped in the code.
 *
 * Pure rules: which column of the file feeds which field, and what one row
 * would do to the table, so it can all be shown before anything is written.
 */
import { z } from 'zod';
import { formatDecimal, parseDecimal } from './money.js';
import { entryCodeSchema, nextAutoCode, resolveLookups, } from './reference-tables.js';
/** Yes/no fields of an entry, optional in the file: without their column they do not change. */
export const ENTRY_FLAGS = [
    { key: 'active', label: 'Attiva' },
    { key: 'isDefault', label: 'Predefinita' },
    { key: 'hidden', label: 'Nascosta' },
];
/** Fields of an entry the file can fill, besides the columns of the table. */
export const ENTRY_FIELDS = [
    { key: 'code', label: 'Codice' },
    { key: 'description', label: 'Descrizione' },
    // The state of the entry, as «Esporta CSV» writes it: the export comes back whole.
    ...ENTRY_FLAGS,
];
/**
 * Proposes a column of the file for every field, matching the labels: the same
 * text, ignoring case, spaces and accents, or one contained in the other.
 */
export function proposeMapping(columns, fileColumns, codeAliases = []) {
    const mapping = {};
    const fields = [
        { key: 'code', label: 'Codice', aliases: codeAliases },
        { key: 'description', label: 'Descrizione', aliases: [] },
        ...ENTRY_FLAGS.map((flag) => ({ key: flag.key, label: flag.label, aliases: [] })),
        ...columns.map((column) => ({
            key: column.key,
            label: column.label,
            aliases: column.aliases ?? [],
        })),
    ];
    const taken = new Set();
    for (const field of fields) {
        const wanted = [field.label, ...field.aliases].map(normalise);
        const free = fileColumns.filter((column) => !taken.has(column));
        const exact = free.find((column) => wanted.includes(normalise(column)));
        const partial = free.find((column) => {
            const name = normalise(column);
            return wanted.some((want) => name.includes(want) || want.includes(name));
        });
        const chosen = exact ?? partial;
        if (chosen) {
            mapping[field.key] = chosen;
            taken.add(chosen);
        }
    }
    return mapping;
}
/**
 * Reads the rows of the file with the chosen mapping and says what each one
 * would do: a new entry, a change to one that is there, nothing, or an error.
 */
export function previewEntries(rows, columns, mapping, existing, lookups = {}, 
/** Prefix of the automatic codes: a row without a code gets the next one. */
autoCode = null) {
    const seen = new Map();
    const taken = new Set(existing.map((item) => item.code));
    let defaultLine = null;
    return rows.map((row, index) => {
        const line = index + 2;
        const raw = (field) => {
            const column = mapping[field];
            return column === undefined ? undefined : row[column];
        };
        let code = text(raw('code'));
        if (!code && autoCode) {
            code = nextAutoCode(autoCode, taken);
            taken.add(code);
        }
        const entry = {
            line,
            code,
            description: text(raw('description')) || code,
            values: {},
            flags: {},
            action: 'new',
            message: null,
        };
        if (!code)
            return { ...entry, action: 'error', message: 'Codice mancante' };
        const parsedCode = entryCodeSchema.safeParse(code);
        if (!parsedCode.success) {
            return {
                ...entry,
                action: 'error',
                message: parsedCode.error.issues[0]?.message ?? 'Codice non valido',
            };
        }
        entry.code = parsedCode.data;
        if (seen.has(entry.code)) {
            return {
                ...entry,
                action: 'error',
                message: `Codice ripetuto nel file (riga ${seen.get(entry.code)})`,
            };
        }
        seen.set(entry.code, line);
        for (const flag of ENTRY_FLAGS) {
            if (mapping[flag.key] === undefined)
                continue;
            const value = readValue(raw(flag.key), { key: flag.key, label: flag.label, type: 'boolean' });
            // An empty cell leaves the state as it is.
            if (value === null)
                continue;
            if (value === 'invalid') {
                return { ...entry, action: 'error', message: `${flag.label}: valore non valido` };
            }
            entry.flags[flag.key] = value;
        }
        if (entry.flags.isDefault) {
            if (defaultLine !== null) {
                return {
                    ...entry,
                    action: 'error',
                    message: `Una sola voce può essere predefinita (già la riga ${defaultLine})`,
                };
            }
            defaultLine = line;
        }
        for (const column of columns) {
            const value = readValue(raw(column.key), column);
            if (value === 'invalid') {
                return {
                    ...entry,
                    action: 'error',
                    message: `${column.label}: valore non valido`,
                };
            }
            entry.values[column.key] = value;
        }
        // Columns tied to another table: the code or the name, both are fine.
        const resolved = resolveLookups(columns, entry.values, lookups);
        const [wrong] = Object.keys(resolved.errors);
        if (wrong) {
            const column = columns.find((item) => item.key === wrong);
            return { ...entry, action: 'error', message: `${column.label}: ${resolved.errors[wrong]}` };
        }
        entry.values = resolved.values;
        const before = existing.find((item) => item.code === entry.code);
        if (before?.locked && entry.flags.active === false) {
            return {
                ...entry,
                action: 'error',
                message: 'Voce usata dalle regole del programma: non si disattiva',
            };
        }
        if (!before)
            return entry;
        const same = before.description === entry.description &&
            columns.every((column) => equal(before.values[column.key] ?? null, entry.values[column.key] ?? null)) &&
            ENTRY_FLAGS.every((flag) => entry.flags[flag.key] === undefined || entry.flags[flag.key] === before[flag.key]);
        return { ...entry, action: same ? 'unchanged' : 'update' };
    });
}
/** Rows that would change the table: what the import writes. */
export function importable(entries) {
    return entries.filter((entry) => entry.action === 'new' || entry.action === 'update');
}
function readValue(value, column) {
    if (value === undefined || value === null || value === '')
        return null;
    const asText = text(value);
    switch (column.type) {
        case 'number':
        case 'money': {
            const number = toNumber(asText, value);
            return number === null ? 'invalid' : number;
        }
        case 'boolean': {
            const yes = ['si', 'sì', 'true', 'vero', 'x', '1'];
            const no = ['no', 'false', 'falso', '0', ''];
            const normalised = normalise(asText);
            if (yes.includes(normalised))
                return true;
            if (no.includes(normalised))
                return false;
            return 'invalid';
        }
        case 'date': {
            if (value instanceof Date)
                return value.toISOString().slice(0, 10);
            const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(asText) ?? null;
            if (match)
                return asText.slice(0, 10);
            const italian = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(asText);
            if (!italian)
                return 'invalid';
            const [, day, month, year] = italian;
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        case 'select':
            return column.options?.find((option) => normalise(option) === normalise(asText)) ?? 'invalid';
        case 'tags': {
            const parts = asText
                .split(/[;,]/)
                .map((part) => part.trim())
                .filter(Boolean);
            const found = parts.map((part) => column.options?.find((option) => normalise(option) === normalise(part)) ?? null);
            return found.some((item) => item === null) ? 'invalid' : found;
        }
        default:
            return asText;
    }
}
function toNumber(asText, value) {
    // "1.234,56", "1234,56" and "1234.56" all come out of a spreadsheet.
    return parseDecimal(typeof value === 'number' ? value : asText);
}
function text(value) {
    if (value === undefined || value === null)
        return '';
    if (value instanceof Date)
        return value.toISOString().slice(0, 10);
    return String(value).trim();
}
function normalise(value) {
    return value
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '');
}
function equal(a, b) {
    if (Array.isArray(a) || Array.isArray(b))
        return JSON.stringify(a) === JSON.stringify(b);
    return a === b;
}
/* --- Requests ------------------------------------------------------------------ */
/** Body of `POST /api/tables/:key/entries/import/preview`: the file, base64. */
export const tableImportPreviewSchema = z.object({
    file: z.string().min(1, { message: 'Scegli un file' }).max(16_000_000),
    /** Chosen by the user; without it the columns are matched by their headings. */
    mapping: z.record(z.string(), z.string()).optional(),
});
/** Body of `POST /api/tables/:key/entries/import`: the file, the mapping and the rows chosen. */
export const tableImportSchema = tableImportPreviewSchema.extend({
    mapping: z.record(z.string(), z.string()),
    lines: z.array(z.number().int().positive()).min(1, { message: 'Scegli almeno una riga' }),
});
/**
 * A value as «Esporta CSV» writes it, for Excel and for a new import to read it
 * back as it was: codes of other tables as codes, numbers with the comma and
 * no thousands dot, dates as «31/12/2026», yes/no as «Sì»/«No».
 */
export function fileValue(column, value) {
    if (value === null || value === undefined || value === '')
        return '';
    switch (column.type) {
        case 'boolean':
            return value ? 'Sì' : 'No';
        case 'money':
        case 'number': {
            const number = parseDecimal(typeof value === 'number' ? value : String(value));
            if (number === null)
                return String(value);
            return formatDecimal(number, 0, column.type === 'money' ? 2 : 4, false);
        }
        case 'date': {
            const [year, month, day] = String(value).split('-');
            return day ? `${day}/${month}/${year}` : String(value);
        }
        case 'tags':
            return Array.isArray(value) ? value.join(', ') : String(value);
        default:
            return String(value);
    }
}
/** The entries of a table as the CSV of «Esporta CSV»: «;» separated, headings in the first row. */
export function entriesToCsv(columns, entries) {
    const header = [
        'Codice',
        'Descrizione',
        ...columns.map((column) => column.label),
        ...ENTRY_FLAGS.map((flag) => flag.label),
    ];
    const rows = entries.map((entry) => [
        entry.code,
        entry.description,
        ...columns.map((column) => fileValue(column, entry.values[column.key])),
        ...ENTRY_FLAGS.map((flag) => (entry[flag.key] ? 'Sì' : 'No')),
    ]);
    return [header, ...rows]
        .map((row) => row.map((cell) => (/[";\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell)).join(';'))
        .join('\r\n');
}
//# sourceMappingURL=table-import.js.map